import axios from 'axios';
import { API_CONFIG } from '../config/api.config';
import tokenStorage from './tokenStorage';

// Flag to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  withCredentials: API_CONFIG.withCredentials,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach access token to every request
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = tokenStorage.get();
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth endpoints that legitimately return 401 for invalid credentials.
// A 401 from these must NOT trigger the refresh flow — otherwise the real
// error (e.g. "Neispravni kredencijali") gets replaced by the refresh-token
// error ("Refresh token nije pronađen").
const AUTH_ENDPOINTS_WITHOUT_REFRESH = [
  '/api/auth/login',
  '/api/auth/superadmin/login',
  '/api/auth/set-password',
  '/api/auth/register',
  '/api/auth/regenerate-token',
];

// Response interceptor - handle 401 errors and refresh token
axiosInstance.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is not 401 or request is already retried, reject immediately
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Let 401s from auth endpoints (invalid credentials, etc.) propagate
    // directly to the caller instead of attempting a token refresh.
    if (AUTH_ENDPOINTS_WITHOUT_REFRESH.some((url) => originalRequest.url?.includes(url))) {
      return Promise.reject(error);
    }

    // A request that was sent WITHOUT an access token cannot be repaired by a
    // refresh. This happens during auth bootstrap: components rendered by Shell
    // can fire before AuthContext obtains the token (e.g. AccountMenu's
    // /api/Employee/me). Reject silently — do NOT refresh, clear the token or
    // redirect, otherwise the in-flight bootstrap refresh gets aborted and a
    // rotated (already revoked) cookie stays in the browser.
    if (!originalRequest.headers?.Authorization) {
      return Promise.reject(error);
    }

    // If this is a refresh request that failed, logout user
    if (originalRequest.url?.includes('/api/auth/refresh')) {
      isRefreshing = false;
      processQueue(error, null);
      
      // Clear access token and redirect to login
      tokenStorage.clear();
      window.location.href = '/login';
      
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    // Mark as retrying and start refresh process
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Single source of truth: refreshAccessToken() handles the refresh API
      // call, token parsing (ApiResponse format) and token storage. It is a
      // standalone module function (not an object method) so calling it here
      // does not depend on `this`. It uses a plain axios call (not
      // axiosInstance) so it won't re-trigger this interceptor.
      const { refreshAccessToken } = await import('../services/authService');
      const accessToken = await refreshAccessToken();

      // Update authorization header
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;

      // Process queued requests
      processQueue(null, accessToken);

      // Reset refreshing flag
      isRefreshing = false;

      // Retry original request
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      // Refresh failed, process queue with error
      processQueue(refreshError, null);
      isRefreshing = false;

      // Clear access token and redirect to login
      tokenStorage.clear();
      window.location.href = '/login';

      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
